/**
 * Sequential color ramps tuned to the brand palette (#04648d, #98c262, #fff).
 * Each ramp is 9 stops; we sample down for 3..9-bin choropleths.
 */
export const RAMPS = {
  blue:       ['#f0f6fa','#dbe8f1','#bcd2e3','#9ab8d2','#7a9bbb','#5b80a4','#3e6a8e','#04648d','#053f5a'],
  green:      ['#f3f8e9','#e1eeca','#cee2a8','#bbd690','#a7ca78','#98c262','#82ad4b','#688a3b','#46622a'],
  teal:       ['#eef6fb','#d6e9f1','#bcdce6','#9ecddb','#7cb9cd','#62acc4','#4ea3ce','#327c9f','#1e5470'],
  amber:      ['#faf1da','#f4e3b6','#ecd28c','#e3c069','#d6ad4d','#c4992b','#b27f1c','#8c601c','#5e4017'],
  coral:      ['#fbe7e3','#f7cfc6','#f0b3a7','#e69384','#d65a51','#bf4940','#9f3a32','#7c2c25','#54201a'],
  diverging:  ['#04648d','#5b80a4','#9ab8d2','#cfdfd1','#e6f0d2','#cee2a8','#a7ca78','#82ad4b','#46622a'],
};

export const RAMP_LABELS = {
  blue:       'Blue (primary)',
  green:      'Green (accent)',
  teal:       'Teal (info)',
  amber:      'Amber',
  coral:      'Coral',
  diverging:  'Diverging (blue ↔ green)',
};

/** Sample n colors evenly from a 9-stop ramp. */
export function sampleRamp(name, n) {
  const ramp = RAMPS[name] || RAMPS.sand;
  if (n >= ramp.length) return ramp.slice();
  if (n <= 1) return [ramp[Math.floor(ramp.length / 2)]];
  return Array.from({ length: n }, (_, i) =>
    ramp[Math.round(i * (ramp.length - 1) / (n - 1))]
  );
}

/**
 * Build a MapLibre `step` paint expression from breaks + colors.
 * Returns a value safe to pass to map.setPaintProperty.
 *
 *   breaks = [b1, b2, ..., b(n-1)]   (length = bins - 1)
 *   colors = [c0, c1, ..., c(n-1)]   (length = bins)
 *   ⇒ ['step', ['get', col],
 *        c0, b1, c1, b2, c2, ..., b(n-1), c(n-1)]
 */
export function stepExpression(column, breaks, values) {
  const expr = ['step', ['to-number', ['get', column]], values[0]];
  for (let i = 0; i < breaks.length; i++) {
    expr.push(breaks[i], values[i + 1]);
  }
  return expr;
}

/**
 * Generate `n` evenly-spaced numeric values between min and max.
 * Used for size-by-value (radius / line-width).
 */
export function rampedSizes(min, max, n) {
  if (n <= 1) return [min];
  return Array.from({ length: n }, (_, i) =>
    +(min + (max - min) * i / (n - 1)).toFixed(2)
  );
}

/**
 * Distinct categorical hues anchored to the brand. Cycles if more
 * categories than swatches.
 */
export const CATEGORICAL_PALETTE = [
  '#04648d', '#98c262', '#4ea3ce', '#e0a93a',
  '#d65a51', '#7a9bbb', '#b8d290', '#5b7d8c',
  '#327c9f', '#688a3b', '#bf4940', '#243f51',
];

export function pickCategoricalColor(index) {
  return CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];
}

/**
 * MapLibre `match` paint expression for categorical coloring.
 *
 *   mappings = [{ value: 'Teaching', color: '#c8956d' }, ...]
 *   defaultColor = fallback for unmapped / new values
 */
export function matchExpression(column, mappings, defaultColor = '#8e8b7e') {
  const expr = ['match', ['to-string', ['get', column]]];
  const seen = new Set();
  for (const m of mappings) {
    const key = String(m.value);
    if (seen.has(key)) continue;
    seen.add(key);
    expr.push(key, m.color);
  }
  expr.push(defaultColor);
  return expr;
}

// ---------------------------------------------------------------------------
// Bivariate palettes — 3x3 grids. Rows = column-A bins (low → high),
// columns = column-B bins (low → high). Names follow Stevens' / Brewer's
// naming for instant recognition.
// ---------------------------------------------------------------------------

export const BIVARIATE_PALETTES = {
  'pink-cyan': [
    ['#e8e8e8', '#dfb0d6', '#be64ac'],
    ['#ace4e4', '#a5add3', '#8c62aa'],
    ['#5ac8c8', '#5698b9', '#3b4994'],
  ],
  'brown-blue-green': [
    ['#e8e8e8', '#b8d6be', '#73ae80'],
    ['#b5c0da', '#90b2b3', '#5a9178'],
    ['#6c83b5', '#567994', '#2a5a5b'],
  ],
  'orange-blue': [
    ['#fef1e4', '#fae6b1', '#f3b300'],
    ['#b0d5df', '#90b2b3', '#b3825c'],
    ['#509dc2', '#516481', '#673838'],
  ],
  'teal-amber': [
    ['#f3f3f3', '#f8d4a4', '#e09d3a'],
    ['#a8d8d6', '#9eb09f', '#a87a4f'],
    ['#04648d', '#3c6f7a', '#5a4f4d'],
  ],
};

export const BIVARIATE_PALETTE_LABELS = {
  'pink-cyan':        'Pink / Cyan',
  'brown-blue-green': 'Brown / Blue / Green',
  'orange-blue':      'Orange / Blue',
  'teal-amber':       'Teal / Amber',
};

/**
 * Build a MapLibre paint expression that combines two `step` classifications
 * into one of `palette.length²` colors.
 *
 *   columnA, breaksA  → row index (0..N-1)   palette[row][col]
 *   columnB, breaksB  → col index (0..N-1)
 *
 * `breaksA` and `breaksB` are inner break edges (length = N-1). Values < the
 * first break go in bin 0, values ≥ last break go in bin N-1.
 */
export function bivariateExpression(columnA, breaksA, columnB, breaksB, palette) {
  const N = palette.length;
  const stepArgs = (col, breaks) => {
    const args = ['step', ['to-number', ['get', col]], 0];
    for (let i = 0; i < breaks.length; i++) {
      args.push(breaks[i], i + 1);
    }
    return args;
  };
  const stepA = stepArgs(columnA, breaksA);
  const stepB = stepArgs(columnB, breaksB);
  // Combined index = row * N + col.
  const idx = ['+', ['*', stepA, N], stepB];

  const matchArgs = ['match', idx];
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      matchArgs.push(r * N + c, palette[r][c]);
    }
  }
  matchArgs.push('#999999');   // fallback for null / non-numeric
  return matchArgs;
}
